import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Instagram, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { socials } from "../lib/data";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/diensten", label: "Diensten" },
  { to: "/werk-in-beeld", label: "Werk in beeld" },
  { to: "/subsidie", label: "Subsidie" },
  { to: "/kosten", label: "Kosten" },
  { to: "/over", label: "Over" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const closeMenu = useCallback(() => setMobileOpen(false), []);

  // Sluit menu bij route-wissel (bv. na klik op een nav-link).
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Voorkom scroll van de achtergrond terwijl het menu open is.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Focus-trap + Escape + focus-return naar hamburger.
  useFocusTrap({
    active: mobileOpen,
    containerRef: menuRef,
    onEscape: closeMenu,
    returnFocusRef: toggleButtonRef,
  });

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-md">
      <div className="container-tight flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center">
          <Logo className="h-12 w-auto md:h-14" />
        </Link>

        <nav aria-label="Hoofdnavigatie" className="hidden items-center gap-0.5 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              activeProps={{ className: "text-primary bg-secondary/60" }}
              activeOptions={{ exact: link.to === "/" }}
              className="inline-flex h-9 items-center justify-center whitespace-nowrap rounded-md px-2.5 text-sm font-medium leading-none tracking-[-0.01em] text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground active:bg-secondary/80"
            >
              {link.label}
            </Link>
          ))}
          <a
            href={socials.instagram.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Fedor Timmerwerken op Instagram (${socials.instagram.handle})`}
            className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-secondary hover:text-primary"
          >
            <Instagram className="h-5 w-5" aria-hidden="true" />
          </a>
          <Link
            to="/contact"
            className="ml-2 inline-flex h-9 items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 text-sm font-semibold leading-none text-primary-foreground transition-colors hover:bg-primary/90 active:bg-primary/80"
          >
            Offerte aanvragen
          </Link>
          <ThemeToggle className="ml-2" />
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <a
            href={socials.instagram.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Fedor Timmerwerken op Instagram (${socials.instagram.handle})`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-foreground transition-colors hover:text-primary"
          >
            <Instagram className="h-5 w-5" aria-hidden="true" />
          </a>

          <ThemeToggle className="h-11 w-11" />


          <button
            ref={toggleButtonRef}
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-foreground"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={mobileOpen ? "Menu sluiten" : "Menu openen"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          ref={menuRef}
          id="mobile-nav"
          role="dialog"
          aria-modal="true"
          aria-label="Mobiele navigatie"
          className="border-t border-border/60 md:hidden"
        >
          <nav aria-label="Mobiele navigatie" className="container-tight flex flex-col gap-1 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMenu}
                activeProps={{ className: "text-primary bg-secondary/60" }}
                activeOptions={{ exact: link.to === "/" }}
                className="flex min-h-12 items-center rounded-md px-3 text-base font-medium tracking-[-0.01em] text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground active:bg-secondary/80"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={socials.instagram.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
              className="flex min-h-12 items-center gap-3 rounded-md px-3 text-base font-medium text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground"
            >
              <Instagram className="h-5 w-5 text-primary" aria-hidden="true" />
              Instagram {socials.instagram.handle}
            </a>
            <Link
              to="/contact"
              onClick={closeMenu}
              className="mt-2 inline-flex min-h-12 items-center justify-center rounded-md bg-primary px-4 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90 active:bg-primary/80"
            >
              Offerte aanvragen
            </Link>



          </nav>
        </div>
      )}
    </header>
  );
}
